// pages.json 消费统一走这里（默认导入 + 解构兜底，避免 h5 端缺失 tabBar 时 ESM 具名导入报错）
import { tabBar } from './pages-json'

/**
 * 统一页面跳转封装（v1 utils/navigator 的改进版：栈深判断挪到调用时刻、back 增加兜底）
 *
 * 背景：微信小程序页面栈上限 10 层，满栈后 uni.navigateTo 直接失败
 * （navigateTo:fail webview count limit exceed）；且栈内每个页面都是完整
 * webview 实例，堆叠过多在低端机上易因内存不足被微信杀进程。
 *
 * 约定：业务代码跳转/回退一律走 routerPush / routerBack，不要裸调
 * uni.navigateTo / uni.navigateBack；routerPush 对 tab 页自动分流 switchTab，
 * 无需调用方区分。redirectTo / reLaunch / switchTab 不增加栈深，需要
 * 明确语义（如登录后替换当前页）时仍可直接使用。
 *
 * 不做「目标页已在栈中则 navigateBack 回跳」的去重：同 URL 不代表同内容
 * （如编辑→预览环，回跳会命中保存前的旧实例造成脏数据），且真实深链
 * （房源→小区→另一房源…）URL 各不相同，去重命中率极低。
 */

/** 页面栈降级阈值：栈深达到该值后 push 降级为原地替换（官方上限 10，留 1 层余量给登录页等突发插入） */
const STACK_THRESHOLD = 9

// —— 防连点导航锁：微信对转场动画期间的重复 navigateTo 不去重，快速连点会
// 叠出多个相同页面（还会加速触顶 10 层栈限制）。跳转发起到 complete 回调期间
// 持锁拦截后续 routerPush/routerBack 调用，锁的时长即真实转场时长 ——

let navigating = false
let navUnlockTimer: ReturnType<typeof setTimeout> | null = null

/** 尝试获取导航锁：已有跳转进行中返回 false（本次调用应直接忽略） */
function acquireNavLock(): boolean {
  if (navigating)
    return false
  navigating = true
  // 兜底：complete 意外不回调时 800ms 自动解锁，避免永久锁死无法跳转
  navUnlockTimer = setTimeout(releaseNavLock, 800)
  return true
}

function releaseNavLock() {
  navigating = false
  if (navUnlockTimer) {
    clearTimeout(navUnlockTimer)
    navUnlockTimer = null
  }
}

/** 无页可退（分享卡片/扫码直达）时的默认兜底落点：首页 tab */
const HOME_PATH = '/pages/index/index'

interface RouterPushOptions {
  /** 目标页绝对路径，可带 query，如 `/pages-sub-home/house-detail/index?id=1` */
  url: string
  /** 跳转失败回调（navigateTo / redirectTo 均生效） */
  fail?: (err: unknown) => void
}

/**
 * 统一跳转：tab 页自动分流 switchTab；普通页正常 uni.navigateTo 入栈，
 * 栈深达到阈值时降级 uni.redirectTo 原地替换——保证永远跳得动，
 * 代价是超深链路上回退时少中间几层
 */
export function routerPush(options: RouterPushOptions): void {
  // 防连点：上一次跳转还在转场中，忽略本次
  if (!acquireNavLock())
    return
  // tab 页只能 switchTab 打开（navigateTo/redirectTo 均直接失败）；
  // switchTab 的 url 不允许带 query，剥掉并在 dev 下告警，参数请改走 store 等渠道
  if (isTabBarPath(options.url)) {
    const [path, query] = options.url.split('?')
    if (query && import.meta.env.DEV)
      console.warn(`[routerPush] tab 页不支持 query 传参，已忽略：${options.url}`)
    uni.switchTab({ ...options, url: path, complete: releaseNavLock })
    return
  }
  if (getCurrentPages().length >= STACK_THRESHOLD) {
    uni.redirectTo({ ...options, complete: releaseNavLock })
    return
  }
  uni.navigateTo({ ...options, complete: releaseNavLock })
}

/**
 * 重复点击当前 tab 的全局事件名（custom-tabbar 广播 pagePath）。
 * 首页/工作台滚动发生在 z-paging 内部 scroll-view，uni.pageScrollTo 对其无效，
 * z-paging tab 页监听此事件后自行调 paging.scrollToTop() 回顶。
 */
export const TABBAR_REPEAT_CLICK_EVENT = 'tabbar-repeat-click'

/** 判断路径是否为 tabBar 页（query 会被剥掉后匹配），跳转分流 switchTab / navigateTo 用 */
export function isTabBarPath(url: string): boolean {
  if (!tabBar || !tabBar.list.length) {
    return false
  }
  const path = url.split('?')[0].replace(/^\//, '')
  return tabBar.list.some(item => item.pagePath === path)
}

interface RouterBackOptions {
  /** 回退层数，默认 1；超出栈深时收敛到栈底页 */
  delta?: number
  /** 无页可退时的兜底落点，默认首页；tab 页走 switchTab，其余 reLaunch */
  fallbackUrl?: string
}

/**
 * 返回上一页：有页可退则 uni.navigateBack；分享卡片/扫码直达等
 * 栈中只有当前页的场景，兜底跳 fallbackUrl，避免返回键点了没反应
 */
export function routerBack(options: RouterBackOptions = {}): void {
  // 防连点：双击返回会连退两层，同样持锁拦截
  if (!acquireNavLock())
    return
  const { delta = 1, fallbackUrl = HOME_PATH } = options
  const stackDepth = getCurrentPages().length
  if (stackDepth > 1) {
    uni.navigateBack({ delta: Math.min(delta, stackDepth - 1), complete: releaseNavLock })
    return
  }
  if (isTabBarPath(fallbackUrl)) {
    uni.switchTab({ url: fallbackUrl, complete: releaseNavLock })
    return
  }
  uni.reLaunch({ url: fallbackUrl, complete: releaseNavLock })
}
